'use client';

import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/solid';
import { Skeleton } from '@mantine/core';
import cn from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { useState, useEffect } from 'react';

import InternalLink from '@/components/InternalLink';
import Loader from '@/components/Loader';
import { NavigationSubcategory, HeaderDataMenu } from '@/utils/types/api';

import styles from './styles.module.scss';
import { NavigationProps } from './types';
import { setLinkLabel } from './utils/labels';

const Navigation = ({
  data,
  subMenuParent,
  openSubMenu,
  closeSubMenu,
  openNavMenu,
  closeNavMenu,
  isMobile,
  isSubMenuOpened,
  isClosedMobileNav,
  closeMobileMenu,
}: NavigationProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleScroll = () => {
    const scrolled = window.scrollY > 175;
    setIsScrolled(scrolled);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    if (isScrolled) {
      closeSubMenu();
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrolled]);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleMenuClick = (url?: string) => () => {
    closeSubMenu();

    if (closeMobileMenu) {
      closeMobileMenu();
    }
  };

  const closeSubcategoriesList = () => {
    closeSubMenu();
    openNavMenu();
  };

  const openSubcategoriesList = (navItem: HeaderDataMenu) => {
    openSubMenu(navItem.Nawigacja.data.attributes);
    closeNavMenu();
  };

  const renderSubcategories = (Subcategory: NavigationSubcategory[]) => {
    const navTree = Subcategory.map(subcategory => {
      const { id, Nazwa, subcategoryItem } = subcategory;

      return (
        <div className={styles.subMenuItem} key={id}>
          {Nazwa && <p>{Nazwa}</p>}
          {renderSubcategoriesItems(subcategoryItem)}
        </div>
      );
    });
    return navTree;
  };
  const renderSubcategoriesItems = (subcategoryItem: any[]) => {
    const linkItemsTree = subcategoryItem.map(subcategory => {
      const { id, Nazwa, Url } = subcategory;

      return (
        <Link
          className={styles.navSubItem}
          href={Url.toLowerCase()}
          key={id}
          onClick={handleMenuClick()}
        >
          {Nazwa}
        </Link>
      );
    });
    return linkItemsTree;
  };

  if (!data) {
    return <Loader />;
  }

  return (
    <div className={styles.container}>
      {data.map(navItem => {
        const attributes = navItem.Nawigacja?.data?.attributes || {};
        const { Etykieta, URL, Podkategoria } = attributes;

        if (!Etykieta || !URL) {
          return null;
        }

        return (
          <div
            className={cn(styles.navTreeWrapper, {
              [styles.activeBorder]:
                isSubMenuOpened &&
                subMenuParent === attributes &&
                Podkategoria?.length > 0,
            })}
            key={navItem.id}
          >
            {Podkategoria?.length > 0 ? (
              isMobile ? (
                <div
                  className={cn(styles.navItem, {
                    [styles.activeItem]:
                      isSubMenuOpened &&
                      subMenuParent === attributes &&
                      Podkategoria?.length > 0,
                    [styles.hidden]: isClosedMobileNav,
                    [styles.orange]: Etykieta === '% Sale',
                  })}
                  onClick={() => openSubcategoriesList(navItem)}
                >
                  {Etykieta}
                  <ChevronRightIcon className={styles.chevronIcon} />
                </div>
              ) : (
                <Link
                  className={cn(styles.navItem, {
                    [styles.activeItem]:
                      isSubMenuOpened &&
                      subMenuParent === attributes &&
                      Podkategoria?.length > 0,
                    [styles.hidden]: isClosedMobileNav,
                    [styles.orange]: Etykieta === '% Sale',
                  })}
                  onMouseEnter={() => openSubMenu(attributes)}
                  href={URL}
                  onClick={handleMenuClick()}
                >
                  {Etykieta}
                </Link>
              )
            ) : (
              <Link
                className={cn(styles.navItem, {
                  [styles.activeItem]:
                    isSubMenuOpened &&
                    subMenuParent === attributes &&
                    Podkategoria?.length > 0,
                  [styles.hidden]: isClosedMobileNav,
                  [styles.orange]: Etykieta === '% Sale',
                })}
                href={URL.toLowerCase()}
                onClick={handleMenuClick(URL.toLowerCase())}
                onMouseEnter={() => openSubMenu(attributes)}
              >
                {Etykieta}
              </Link>
            )}
            {!isMobile ? (
              isSubMenuOpened &&
              subMenuParent === attributes &&
              Podkategoria.length > 0 && (
                <>
                  <div
                    onMouseLeave={closeSubMenu}
                    className={cn(styles.subMenuWrapper, {
                      [styles.opened]: isSubMenuOpened,
                    })}
                  >
                    <nav className={styles.navSubItemsWrapper}>
                      <div className={styles.navSubItems}>
                        <div className={styles.subMenuItems}>
                          {renderSubcategories(Podkategoria)}
                        </div>
                        {attributes.URL !== '#' && (
                          <div className={styles.linkWrapper}>
                            <InternalLink
                              link={attributes.URL.toLowerCase()}
                              label={setLinkLabel(Etykieta)}
                              onClick={handleMenuClick()}
                              withIcon
                              isCapitalize
                            />
                          </div>
                        )}
                      </div>
                      {attributes.Media?.data?.attributes?.url && (
                        <Skeleton
                          visible={isLoading}
                          width="100%"
                          className={styles.categoryImage}
                        >
                          <Image
                            src={attributes.Media.data?.attributes?.url}
                            alt={
                              attributes.TekstAlternatywny
                                ? attributes.TekstAlternatywny
                                : attributes.Media?.data?.attributes
                                    ?.alternativeText
                            }
                            width={600}
                            height={370}
                            onLoad={handleImageLoad}
                          />
                        </Skeleton>
                      )}
                    </nav>
                  </div>
                  <div
                    className={cn(styles.overlay, {
                      [styles.visible]:
                        isSubMenuOpened &&
                        subMenuParent === attributes &&
                        Podkategoria.length > 0,
                    })}
                    onMouseEnter={closeSubMenu}
                  />
                </>
              )
            ) : (
              <div>
                {isSubMenuOpened &&
                  subMenuParent === attributes &&
                  Podkategoria.length > 0 && (
                    <>
                      <div className={styles.openCategoryWrapper}>
                        <div
                          className={styles.openCategory}
                          onClick={closeSubcategoriesList}
                        >
                          <ChevronLeftIcon className={styles.chevronIcon} />
                          {Etykieta}
                        </div>
                        {attributes.URL !== '#' && (
                          <div className={styles.linkWrapper}>
                            <InternalLink
                              link={attributes.URL.toLowerCase()}
                              label="Zobacz wszystkie"
                              isCapitalize
                              withIcon={false}
                              onClick={handleMenuClick()}
                            />
                          </div>
                        )}
                      </div>
                      <div className={styles.subMenuItems}>
                        {renderSubcategories(Podkategoria)}
                      </div>
                    </>
                  )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Navigation;
